/**
 * /videos — lista de videos gerados via HeyGen.
 * Polling 8s pra videos com status nao-terminal.
 */
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import { Film, Loader2, CheckCircle2, AlertCircle, RefreshCw } from 'lucide-react';

type StatusVideo = 'pendente' | 'enviado' | 'processando' | 'concluido' | 'falhou' | 'cancelado';

interface VideoItem {
  id: string;
  texto: string;
  avatarId: string;
  voiceId: string;
  status: StatusVideo;
  videoUrl: string | null;
  thumbnailUrl: string | null;
  duracaoSeg: number | null;
  custoEstimadoUsd: string | number | null;
  custoUsd: string | number | null;
  erro: string | null;
  transcricaoId: string | null;
  criadoEm: string;
  concluidoEm: string | null;
}

const STATUS_LABEL: Record<StatusVideo, string> = {
  pendente: 'Pendente',
  enviado: 'Enviado',
  processando: 'Processando',
  concluido: 'Concluído',
  falhou: 'Falhou',
  cancelado: 'Cancelado',
};

export default function Videos() {
  const [items, setItems] = useState<VideoItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState<string | null>(null);

  async function carregar() {
    try {
      const r = await api.get<{ items: VideoItem[]; total: number }>('/videos', { take: 100 });
      setItems(r.items);
      setTotal(r.total);
      setErro(null);
    } catch (e) {
      setErro((e as Error).message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    carregar();
    const t = setInterval(carregar, 8000);
    return () => clearInterval(t);
  }, []);

  const ativos = useMemo(
    () => items.filter((v) => ['pendente', 'enviado', 'processando'].includes(v.status)),
    [items],
  );
  const terminados = useMemo(
    () => items.filter((v) => ['concluido', 'falhou', 'cancelado'].includes(v.status)),
    [items],
  );

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="mb-4 flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 flex items-center gap-2">
            <Film className="w-6 h-6 text-purple-600" />
            Vídeos do avatar
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Gerados a partir de transcrições via HeyGen.
          </p>
        </div>
        <button
          onClick={carregar}
          className="p-2 border border-gray-300 dark:border-gray-600 rounded text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
          title="Atualizar"
        >
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>

      {erro && (
        <div className="mb-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded p-3 text-sm text-red-700 dark:text-red-400">
          {erro}
        </div>
      )}

      {ativos.length > 0 && (
        <section className="mb-6">
          <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2 flex items-center gap-2">
            <Loader2 className="w-4 h-4 text-purple-500 animate-spin" />
            Renderizando ({ativos.length})
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {ativos.map((v) => <VideoCard key={v.id} v={v} />)}
          </div>
        </section>
      )}

      <section>
        <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
          {ativos.length > 0 ? 'Concluídos' : 'Vídeos'}
        </h2>
        {loading ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">Carregando...</p>
        ) : terminados.length === 0 ? (
          <div className="bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded p-6 text-center text-sm text-gray-500 dark:text-gray-400">
            Nenhum vídeo gerado ainda. Vá em uma transcrição e clique <strong>Gerar vídeo do avatar</strong>.
          </div>
        ) : (
          <>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">{terminados.length} de {total}</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {terminados.map((v) => <VideoCard key={v.id} v={v} />)}
            </div>
          </>
        )}
      </section>
    </div>
  );
}

function VideoCard({ v }: { v: VideoItem }) {
  const cor =
    v.status === 'concluido' ? 'text-green-600 dark:text-green-400' :
    v.status === 'falhou' ? 'text-red-600 dark:text-red-400' :
    'text-gray-500 dark:text-gray-400';
  const Icon = v.status === 'concluido' ? CheckCircle2 : v.status === 'falhou' ? AlertCircle : Loader2;
  const animado = v.status === 'pendente' || v.status === 'enviado' || v.status === 'processando';

  return (
    <Link
      to={`/videos/${v.id}`}
      className="block bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden hover:border-purple-300 dark:hover:border-purple-600 transition-colors"
    >
      <div className="aspect-video bg-gray-100 dark:bg-gray-900 relative flex items-center justify-center">
        {v.thumbnailUrl ? (
          <img src={v.thumbnailUrl} alt="" className="w-full h-full object-cover" loading="lazy" />
        ) : (
          <Film className="w-10 h-10 text-gray-400" />
        )}
        {v.duracaoSeg && (
          <span className="absolute bottom-1 right-1 text-xs bg-black/70 text-white px-1.5 py-0.5 rounded font-mono">
            {Math.floor(v.duracaoSeg / 60)}:{String(v.duracaoSeg % 60).padStart(2, '0')}
          </span>
        )}
      </div>
      <div className="p-3">
        <p className="text-sm text-gray-900 dark:text-gray-100 line-clamp-2 mb-1">
          {v.texto.slice(0, 100)}{v.texto.length > 100 ? '…' : ''}
        </p>
        <div className="flex items-center gap-2 text-xs">
          <Icon className={`w-3.5 h-3.5 ${cor} ${animado ? 'animate-spin' : ''}`} />
          <span className={cor}>{STATUS_LABEL[v.status]}</span>
          {v.custoUsd && (
            <span className="text-gray-400">· ${Number(v.custoUsd).toFixed(4)}</span>
          )}
          {!v.custoUsd && v.custoEstimadoUsd && (
            <span className="text-gray-400">· ~${Number(v.custoEstimadoUsd).toFixed(4)}</span>
          )}
        </div>
      </div>
    </Link>
  );
}
